import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["leaseStatus"];
  static values = {
    heartbeatUrl: String,
    releaseUrl: String,
    editLeaseToken: String,
  };

  connect() {
    this.setupJiraIssueCreation();
    if (this.hasHeartbeatUrlValue) {
      this.submitting = false;
      this.leaseLost = false;
      this.heartbeatTimer = window.setInterval(() => this.heartbeat(), 30_000);
      this.submitHandler = () => {
        this.submitting = true;
      };
      this.leaveHandler = () => this.releaseLease();
      this.element.addEventListener("submit", this.submitHandler);
      window.addEventListener("pagehide", this.leaveHandler);
      document.addEventListener("turbo:before-visit", this.leaveHandler);
    }
  }

  disconnect() {
    window.clearInterval(this.heartbeatTimer);
    this.element.removeEventListener("submit", this.submitHandler);
    window.removeEventListener("pagehide", this.leaveHandler);
    document.removeEventListener("turbo:before-visit", this.leaveHandler);
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content;
  }

  heartbeat() {
    if (this.submitting || this.leaseLost) return;

    return fetch(this.heartbeatUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      // eslint-disable-next-line camelcase
      body: JSON.stringify({ edit_lease_token: this.editLeaseTokenValue }),
    })
      .then((response) => {
        if (this.leaseLost) return;

        if (response.status === 409 || response.status === 404) {
          this.leaseWasLost();
        } else if (!response.ok) {
          this.heartbeatUnavailable();
        } else if (this.hasLeaseStatusTarget) {
          this.leaseStatusTarget.classList.replace(
            "alert-warning",
            "alert-info",
          );
          this.leaseStatusTarget.textContent =
            "You have the edit lease for this Test Report. It is renewed while this page is open.";
        }
      })
      .catch(() => this.heartbeatUnavailable());
  }

  heartbeatUnavailable() {
    if (this.leaseLost || !this.hasLeaseStatusTarget) return;

    this.leaseStatusTarget.classList.replace("alert-info", "alert-warning");
    this.leaseStatusTarget.textContent =
      "The connection was interrupted. Your changes are still here; the lease will be checked when you save.";
  }

  releaseLease() {
    if (this.submitting || !this.hasReleaseUrlValue) return;

    fetch(this.releaseUrlValue, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      // eslint-disable-next-line camelcase
      body: JSON.stringify({ edit_lease_token: this.editLeaseTokenValue }),
      keepalive: true,
    }).catch(() => {});
  }

  leaseWasLost() {
    this.leaseLost = true;
    window.clearInterval(this.heartbeatTimer);
    this.element
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((control) => {
        control.disabled = true;
      });
    if (this.hasLeaseStatusTarget) {
      this.leaseStatusTarget.classList.replace("alert-info", "alert-danger");
      this.leaseStatusTarget.textContent =
        "This edit lease has expired or was released. Copy your unsaved changes before leaving this page.";
    }
  }

  setupJiraIssueCreation() {
    this.element
      .querySelectorAll("[data-turbo-method='post']")
      .forEach((link) => {
        link.addEventListener("click", this.handleJiraIssueCreation.bind(this));
      });
  }

  handleJiraIssueCreation(event) {
    event.preventDefault();
    const select = event.target.previousElementSibling;
    const projectKey = select.value;
    const url = event.target.getAttribute("href");

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      // eslint-disable-next-line camelcase
      body: JSON.stringify({ project_key: projectKey }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          alert("Jira issue created successfully");
          window.location.reload();
        } else {
          alert("Failed to create Jira issue: " + data.error);
        }
      })
      .catch((error) => {
        alert("Failed to create Jira issue" + error);
      });
  }

  scrollToNextUntested(event) {
    const results = document.querySelectorAll(".test-result");
    const nextUntested = [...results].find(
      (result) => this.statusForResult(result) === "untested",
    );
    if (nextUntested) {
      nextUntested.scrollIntoView();
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
    event.preventDefault();
  }

  statusChanged(event) {
    const result = event.target.closest(".test-result");
    this.updateColor(result);
  }

  updateColor(result) {
    result.classList.remove(
      "bg-success-subtle",
      "bg-danger-subtle",
      "bg-secondary-subtle",
    );
    const status = this.statusForResult(result);
    if (status === "untested") result.classList.add("bg-secondary-subtle");
    if (status === "failed") result.classList.add("bg-danger-subtle");
    if (status === "passed") result.classList.add("bg-success-subtle");
  }

  statusForResult(result) {
    const steps = result.querySelectorAll(".test-step");
    const status = [...steps].map((step) => {
      return this.statusForStep(step);
    });

    if (status.includes("failed")) {
      return "failed";
    }

    if (status.includes("untested")) {
      return "untested";
    }

    return "passed";
  }

  statusForStep(step) {
    const passed = step.querySelector('input[value="passed"]:checked');
    const failed = step.querySelector('input[value="failed"]:checked');
    const skipped = step.querySelector('input[value="skipped"]:checked');

    if (passed || skipped) {
      return "passed";
    } else if (failed) {
      return "failed";
    } else {
      return "untested";
    }
  }
}
